import React, { useEffect, useState } from 'react';
import {
  ListItem,
  ListItemText,
  Collapse,
  List,
  Box,
  Tooltip,
  IconButton,
  TextField,
} from '@mui/material';
import { Close, ExpandLess, ExpandMore, Search } from '@mui/icons-material';
import sourceIcon from './images/source.png';
import salesForce from './images/salesforce.png';
import mySql from './images/sql.png';
import oracle from './images/oracle.png';
import hubspot from './images/hubspot.png';
import github from './images/github.png';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';

const listVars = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.1 },
  },
};
const itemVars = {
  hidden: { y: 8, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.25, ease: 'easeOut' } },
};

const SourcePanel = ({ clearTrigger }) => {
  const [selectedItems, setSelectedItems] = useState([]);

  const [openSource, setOpenSource] = useState(true);
  const sources = [
    { name: 'Salesforce', sourceType: 'Salesforce', icon: salesForce },
    { name: 'SQL', sourceType: 'SQL', icon: mySql },
    { name: 'Oracle', sourceType: 'Oracle', icon: oracle },
    { name: 'HubSpot', sourceType: 'HubSpot', icon: hubspot },
    { name: 'GitHub', sourceType: 'GitHub', icon: github },
  ];

  const [showSearch, setShowSearch] = useState(false);
  const [q, setQ] = useState('');
  const filtered = sources.filter((s) =>
    s.name.toLowerCase().includes(q.toLowerCase()),
  );

  useEffect(() => {
    if (clearTrigger) setSelectedItems([]);
  }, [clearTrigger]);

  const handleItemClick = (e, itemName) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      setSelectedItems((prev) =>
        prev.includes(itemName)
          ? prev.filter((name) => name !== itemName)
          : [...prev, itemName],
      );
    } else {
      setSelectedItems([itemName]);
    }
  };

  // const handleDragStart = (e, source) => {
  //   e.dataTransfer.setData('application/reactflow', source.name);
  //   e.dataTransfer.setData('nodeType', 'Source');
  //   e.dataTransfer.setData('sourceType', source.sourceType);
  //   e.dataTransfer.effectAllowed = 'move';
  // };

  const handleDragStart = (e, draggedItem) => {
    const baseItems =
      selectedItems.length > 1
        ? sources.filter((s) => selectedItems.includes(s.name))
        : [draggedItem];

    const dragItems = baseItems.map((item) => ({
      ...item,
      label: `${item.name} (Source)`,
      type: 'Source',
      config: {
        ...item,
        label: `${item.name} (Source)`,
        type: 'sourceNode',
      },
    }));

    if (dragItems.length === 1) {
      e.dataTransfer.setData('application/reactflow', dragItems[0].name);
    }

    e.dataTransfer.setData('multi-nodes', JSON.stringify(dragItems));
    e.dataTransfer.setData('nodeType', 'Source');
    e.dataTransfer.effectAllowed = 'move';
  };

  const toggleSource = () => {
    if (openSource) {
      setShowSearch(false);
      setQ('');
    }

    setOpenSource(!openSource);
  };

  return (
    <List disablePadding>
      <ListItem
        button
        onClick={toggleSource}
        sx={{
          background: openSource ? '#5A66A6' : 'transparent',
          color: openSource ? '#fff' : '#000',
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',

          px: 2,
          py: 1,
          fontWeight: 600,
          fontSize: 16,
          mb: 1,
          letterSpacing: 1,
          transition: 'background 0.2s, color 0.2s',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          paddingLeft: '5px',
          '&:hover': {
            background: openSource ? '#4C5B9A' : 'rgba(90, 102, 166, 0.1)',
            color: openSource ? '#fff' : '#000',
          },
        }}
      >
        <img
          src={sourceIcon}
          alt="source"
          width={25}
          height={25}
          style={{
            marginRight: 15,
            filter: openSource ? 'brightness(0) invert(1)' : 'none',
            transition: 'filter 0.2s',
            padding: '4px',
          }}
        />
        <motion.div
          initial={{ y: -8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          <ListItemText
            primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
            primary="Source"
          />
        </motion.div>

        <Box
          sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 0.5 }}
        >
          {openSource && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowSearch((x) => !x);
                if (!openSource) setOpenSource(true);
              }}
              sx={{ mr: 0.5, color: '#fff' }}
            >
              {showSearch ? (
                <Close fontSize="small" />
              ) : (
                <Search fontSize="small" />
              )}
            </IconButton>
          )}
          {openSource ? <ExpandLess /> : <ExpandMore />}
        </Box>
      </ListItem>
      <Collapse in={openSource} timeout="auto" unmountOnExit>
        <motion.div
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
        >
          <Box
            sx={{
              background: '#F7F9FF',
              pb: 1,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          >
            {showSearch && (
              <Box sx={{ px: 2, pt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search sources"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      height: 32,
                      fontSize: 13,
                      px: 1,
                    },
                  }}
                  InputProps={{ sx: { color: '#000' } }}
                />
              </Box>
            )}

            <List
              component={motion.ul}
              variants={listVars}
              initial="hidden"
              animate="show"
              disablePadding
            >
              {filtered.map((item, idx) => (
                <ListItem
                  key={`${item.name}-${idx}`}
                  component={motion.li}
                  variants={itemVars}
                  selected={selectedItems.includes(item.name)}
                  onClick={(e) => handleItemClick(e, item.name)}
                  sx={{
                    pl: 4,
                    py: 0.5,
                    cursor: 'pointer',
                    backgroundColor: selectedItems.includes(item.name)
                      ? 'rgba(0, 86, 210, 0.15)'
                      : 'transparent',

                    '&:hover': {
                      backgroundColor: 'rgba(0, 86, 210, 0.08)',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                    },
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item)}
                >
                  <Tooltip
                    title={`Drag to add ${item.name} source`}
                    placement="right"
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <img
                        src={
                          item.name === 'Salesforce' ? salesForce : item.icon
                        }
                        alt={`${item.name} source`}
                        width={item.name === 'Salesforce' ? 35 : 25}
                        height={25}
                        style={{
                          marginRight: item.name === 'Salesforce' ? 10 : 20,
                        }}
                      />
                      <ListItemText
                        primaryTypographyProps={{
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                        primary={item.name}
                      />
                    </Box>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          </Box>
        </motion.div>
      </Collapse>
    </List>
  );
};

SourcePanel.propTypes = {
  clearTrigger: PropTypes.number,
};

export default SourcePanel;
